import type { ChallengeTypeIcons } from '@/config/objects/events';
import { buildMediaUrl, extractMediaId } from '@/lib/media';

/**
 * Branding model + (de)serialization.
 *
 * Branding (DT-162 / DT-165 / DT-157 / DT-166) is edited on the event's (or
 * game template's) Branding tab and applied by mobile + the leaderboard
 * display when a team joins. This module is the single source of truth for
 * the branding form shape and how it maps to/from the API entity, so the tab
 * editors stay thin. `Event` and `Template` both satisfy `BrandingSource`.
 */

/**
 * Challenge types the map-icon override editor knows about. Mirrors the API's
 * ChallengeType enum exactly — keep in sync.
 */
export const CHALLENGE_ICON_TYPES = [
  'riddle',
  'counting',
  'exploration',
  'search',
  'observation',
  'photo',
  'video',
  'finale',
  'puzzle',
] as const;
export type ChallengeIconType = (typeof CHALLENGE_ICON_TYPES)[number];

export const CHALLENGE_ICON_TYPE_LABELS: Record<ChallengeIconType, string> = {
  riddle: 'Trivia',
  counting: 'Counting',
  exploration: 'Exploration',
  search: 'Search',
  observation: 'Observation',
  photo: 'Photo',
  video: 'Video',
  finale: 'Finale',
  puzzle: 'Puzzle',
};

/**
 * Theme presets the dropdown surfaces. Only `default` is consumed by
 * mobile/display today — the column accepts arbitrary strings so a follow-up
 * can ship `pirate`, `corporate`, etc. without another migration.
 */
export const THEME_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'default', label: 'Default' },
];

/** Valid hex color (3/4/6/8 digits). Empty string means "use the default". */
export const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * What mobile actually renders when a branding color is left blank — shown in
 * the editor so admins pick with the real default in view. Mirrors
 * `useEventBranding.shared.ts` (background → colors.headerDark, accent →
 * colors.primary in the mobile app's constants/colors.ts) — keep in sync.
 */
export const BRANDING_COLOR_DEFAULTS = {
  background: '#1E293B',
  accent: '#3B82F6',
} as const;

export interface BrandingFormData {
  logoId?: string;
  logoSmallId?: string;
  backgroundColor: string;
  accentColor: string;
  theme: string;
  customPointsLabel: string;
  hidePoiOrder: boolean;
  permitActivateOnPin: boolean;
  slideshowIntervalSeconds: number;
  /**
   * Per-challenge-type map-icon overrides; `iconId` (custom image), `icon`
   * (lucide PascalCase name), and `color` are independently optional. A custom
   * image wins over a lucide selection when both are set.
   */
  challengeTypeIcons: Partial<
    Record<ChallengeIconType, { iconId?: string; icon?: string; color?: string }>
  >;
}

export const DEFAULT_BRANDING: BrandingFormData = {
  logoId: undefined,
  logoSmallId: undefined,
  backgroundColor: '',
  accentColor: '',
  theme: 'default',
  customPointsLabel: '',
  hidePoiOrder: false,
  permitActivateOnPin: false,
  slideshowIntervalSeconds: 5,
  challengeTypeIcons: {},
};

/**
 * Structural view of the branding fields shared by `Event` and `Template`.
 * Anything carrying these fields can hydrate the branding form.
 */
export interface BrandingSource {
  logoUrl?: string | null;
  logoSmallUrl?: string | null;
  backgroundColor?: string | null;
  accentColor?: string | null;
  theme?: string;
  customPointsLabel?: string | null;
  hidePoiOrder?: boolean;
  permitActivateOnPin?: boolean;
  slideshowIntervalSeconds?: number;
  challengeTypeIcons?: ChallengeTypeIcons | null;
}

/**
 * Hydrate the branding form from a loaded entity (event or template). The
 * server stores per-type icons as `{ iconUrl, color }`; the form holds
 * `{ iconId, color }` so the upload component can drive its own preview from
 * the media id.
 *
 * @param e - The loaded entity's branding fields.
 * @returns Branding form state.
 */
export function brandingFromEntity(e: BrandingSource): BrandingFormData {
  return {
    logoId: extractMediaId(e.logoUrl),
    logoSmallId: extractMediaId(e.logoSmallUrl),
    backgroundColor: e.backgroundColor ?? '',
    accentColor: e.accentColor ?? '',
    theme: e.theme ?? 'default',
    customPointsLabel: e.customPointsLabel ?? '',
    hidePoiOrder: e.hidePoiOrder ?? false,
    permitActivateOnPin: e.permitActivateOnPin ?? false,
    slideshowIntervalSeconds: e.slideshowIntervalSeconds ?? 5,
    challengeTypeIcons: Object.fromEntries(
      CHALLENGE_ICON_TYPES.map((t) => {
        const entry = e.challengeTypeIcons?.[t];
        if (!entry) return [t, { iconId: undefined, icon: undefined, color: '' }] as const;
        return [
          t,
          {
            iconId: extractMediaId(entry.iconUrl),
            icon: entry.icon ?? undefined,
            color: entry.color ?? '',
          },
        ] as const;
      }),
    ) as BrandingFormData['challengeTypeIcons'],
  };
}

/** Back-compat alias — the event tab predates template branding. */
export const brandingFromEvent = brandingFromEntity;

/**
 * Convert the form's `{ iconId, color }` per-type entries into the server's
 * `{ iconUrl, color }` JSON shape, dropping empty entries so the column stays
 * small. Returns null when no overrides remain (stored as SQL NULL).
 *
 * @param formIcons - The form's per-type icon entries.
 * @returns Server JSON, or null.
 */
function serialiseTypeIconOverrides(
  formIcons: BrandingFormData['challengeTypeIcons'],
): Record<string, { iconUrl?: string | null; icon?: string | null; color?: string | null }> | null {
  const out: Record<
    string,
    { iconUrl?: string | null; icon?: string | null; color?: string | null }
  > = {};
  for (const [type, entry] of Object.entries(formIcons)) {
    if (!entry) continue;
    const iconUrl = buildMediaUrl(entry.iconId);
    const icon = entry.icon || null;
    const color = entry.color?.trim() || null;
    if (!iconUrl && !icon && !color) continue;
    out[type] = { iconUrl, icon, color };
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Map branding form state to the PUT /events/:id payload. Empty strings clear
 * fields (sent as null) so the server stores SQL NULL rather than an empty
 * string a future hex-read would reject.
 *
 * @param form - Branding form state.
 * @returns Partial event payload covering only branding fields.
 */
export function brandingToPayload(form: BrandingFormData): Record<string, unknown> {
  return {
    logoUrl: buildMediaUrl(form.logoId),
    logoSmallUrl: buildMediaUrl(form.logoSmallId),
    backgroundColor: form.backgroundColor.trim() || null,
    accentColor: form.accentColor.trim() || null,
    theme: form.theme || 'default',
    customPointsLabel: form.customPointsLabel.trim() || null,
    hidePoiOrder: form.hidePoiOrder,
    permitActivateOnPin: form.permitActivateOnPin,
    slideshowIntervalSeconds: form.slideshowIntervalSeconds,
    challengeTypeIcons: serialiseTypeIconOverrides(form.challengeTypeIcons),
  };
}

/**
 * True iff any branding field is set away from its default — drives the
 * "Reset Branding to Default" button so it only shows when there's something
 * to reset.
 *
 * @param form - Branding form state.
 */
export function hasAnyBranding(form: BrandingFormData): boolean {
  return !!(
    form.logoId ||
    form.logoSmallId ||
    form.backgroundColor.trim() ||
    form.accentColor.trim() ||
    (form.theme && form.theme !== 'default') ||
    form.customPointsLabel.trim() ||
    form.hidePoiOrder ||
    Object.values(form.challengeTypeIcons).some((entry) => entry?.iconId || entry?.color?.trim())
  );
}

/**
 * Validate branding hex-color fields (the two top-level colors + each per-type
 * override). Empty is valid ("use default"). Keys match the field ids the
 * editor scrolls to: `backgroundColor`, `accentColor`, `typeIconColor.<type>`.
 *
 * @param form - Branding form state.
 * @returns Map of field id -> error message (empty when valid).
 */
export function validateBranding(form: BrandingFormData): Record<string, string> {
  const errors: Record<string, string> = {};
  if (form.backgroundColor && !HEX_COLOR_REGEX.test(form.backgroundColor)) {
    errors.backgroundColor = 'Use a hex color like #1F2937';
  }
  if (form.accentColor && !HEX_COLOR_REGEX.test(form.accentColor)) {
    errors.accentColor = 'Use a hex color like #1F2937';
  }
  for (const type of CHALLENGE_ICON_TYPES) {
    const color = form.challengeTypeIcons[type]?.color?.trim();
    if (color && !HEX_COLOR_REGEX.test(color)) {
      errors[`typeIconColor.${type}`] = 'Use a hex color like #1F2937';
    }
  }
  return errors;
}
